import React, { useState, useEffect } from 'react';
import { 
  UncontrolledDropdown, 
  DropdownToggle, 
  DropdownMenu, 
  DropdownItem 
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../hooks/useAccount';


interface UserRole {
  canManageAccount: boolean;
  canManageUsers: boolean;
  canViewUsage: boolean;
}

export const AccountDropdown: React.FC = () => {
  const { userEmail, logout } = useAuth();
  const navigate = useNavigate();
  const { data: account } = useAccount();
  const [userRole, setUserRole] = useState<UserRole>({
    canManageAccount: false,
    canManageUsers: false,
    canViewUsage: true
  });

  // Set user permissions - in a real app, this would come from the server
  useEffect(() => {
    setUserRole({
      canManageAccount: true, // Assume user has some permissions for now
      canManageUsers: true,   // This would be based on actual role from server
      canViewUsage: true
    });
  }, []);

  const handleAccountSettings = () => {
    navigate('/account/settings');
  };

  const handleUserSettings = () => {
    navigate('/account/profile');
  };

  const handleUsage = () => {
    navigate('/account/usage');
  };

  const handleMembers = () => {
    navigate('/account/members');
  };

  const getUserDisplayName = () => {
    if (userEmail) {
      // Extract name part from email (before @)
      const name = userEmail.split('@')[0];
      // Capitalize first letter of each word
      return name.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ');
    }
    return 'User';
  };

  const getAccountDisplayName = () => {
    if (account?.companyName) {
      return account.companyName;
    }
    if (account?.name) {
      return account.name;
    }
    return 'Personal Account';
  };

  return (
    <UncontrolledDropdown direction="down">
      <DropdownToggle 
        caret
        tag="a"
        className="nav-link d-flex align-items-center"
        style={{ 
          cursor: 'pointer'
        }}
      >
        <div className="d-flex align-items-center">
          <div 
            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2"
            style={{ width: '28px', height: '28px', fontSize: '12px', fontWeight: 'bold' }}
          >
            {getUserDisplayName().split(' ').map(name => name.charAt(0)).join('').slice(0, 2)}
          </div>
          <span className="me-2">{getUserDisplayName()}</span>
        </div>
      </DropdownToggle>
      
      <DropdownMenu 
        end 
        style={{ 
          minWidth: '280px',
          borderRadius: '12px',
          border: '1px solid var(--bs-border-color)',
          boxShadow: 'var(--bs-box-shadow)'
        }}
      >
        {/* Account Section */}
        <div className="px-3 py-3 border-bottom bg-body-secondary">
          <div className="fw-bold text-body mb-1">{getAccountDisplayName()}</div>
          <div className="small text-muted">{userEmail}</div>
          {account && (
            <div className="mt-2">
              <span className="badge bg-secondary me-1">{account.subscriptionLevel}</span>
              {account.hasClaudeApiKey && <span className="badge bg-success me-1">Claude</span>}
              {account.hasOpenAIApiKey && <span className="badge bg-info me-1">OpenAI</span>}
            </div>
          )}
        </div>

        {/* User Actions */}
        <div className="py-1">
          <DropdownItem 
            onClick={handleUserSettings}
            className="px-3 py-2 d-flex align-items-center"
            style={{ fontSize: '14px' }}
          >
            <i className="fas fa-user me-3 text-muted" style={{ width: '16px' }}></i>
            Profile Settings
          </DropdownItem>

          {/* Account Management (if user has permissions) */}
          {userRole.canViewUsage && (
            <DropdownItem 
              onClick={handleUsage}
              className="px-3 py-2 d-flex align-items-center"
              style={{ fontSize: '14px' }}
            >
              <i className="fas fa-chart-bar me-3 text-muted" style={{ width: '16px' }}></i>
              Usage & Billing
            </DropdownItem>
          )}

          {userRole.canManageAccount && (
            <DropdownItem 
              onClick={handleAccountSettings}
              className="px-3 py-2 d-flex align-items-center"
              style={{ fontSize: '14px' }}
            >
              <i className="fas fa-cog me-3 text-muted" style={{ width: '16px' }}></i>
              Account Settings
            </DropdownItem>
          )}

          {userRole.canManageUsers && (
            <DropdownItem 
              onClick={handleMembers}
              className="px-3 py-2 d-flex align-items-center"
              style={{ fontSize: '14px' }}
            >
              <i className="fas fa-users me-3 text-muted" style={{ width: '16px' }}></i>
              Manage Members
            </DropdownItem>
          )}
        </div>

        <div className="dropdown-divider my-1"></div>
        
        {/* Logout */}
        <div className="py-1">
          <DropdownItem 
            onClick={logout}
            className="px-3 py-2 d-flex align-items-center text-danger"
            style={{ fontSize: '14px' }}
          >
            <i className="fas fa-sign-out-alt me-3" style={{ width: '16px' }}></i>
            Logout
          </DropdownItem>
        </div>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};